import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  FormHelperText,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  deleteCvForSubmissions,
  deleteRtrForSubmissions,
  IRightToRepresent,
  ISubmissionSettingsResponse,
} from 'wisestep-api';
import {
  WsButton,
  WsFilePreview,
  WsFileUpload,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import * as yup from 'yup';

import { ConfirmDeleteDialog } from '../../../../components';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { downloadCV } from '../../../conversationWidget/utils/cvHelper';
import { IDialogState, IExcelRecords, initialDialogState } from '../../types';
import {
  createRowsForSubmissions,
  onCVViewClick,
  saveUploadedCV,
  uploadFileToS3,
} from '../../utils/bulkSubmissionsHelper';
import RTR from '../rtr';
interface IBulkSubmissionsForm {
  ignoreFirstRow: boolean;
  fieldMappings: string[];
  uploadCv;
}
interface ISpreadSheetProps {
  candidatesData?: Map<number, IExcelRecords>;
  fileId: string;
  submissionSetting?: ISubmissionSettingsResponse;
  getFieldMappingValues: (columns: string[], ignoreFirstRow: boolean) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  fieldMappings?: string[];
}
type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const ImportSpreadSheetColumns = ({
  candidatesData = new Map(),
  fileId,
  submissionSetting,
  getFieldMappingValues,
  setOpen,
  fieldMappings,
}: ISpreadSheetProps) => {
  const mandatoryColumns: string[] = [];
  const missingFields = useRef<string[]>([]);

  submissionSetting &&
    (Object.entries(submissionSetting) as Entries<ISubmissionSettingsResponse>).forEach((entry) => {
      if (entry?.[1] && entry[1].required) mandatoryColumns.push(entry[0]);
    });

  const methods = useForm<IBulkSubmissionsForm>({
    resolver: yupResolver(
      yup.object().shape({
        uploadCv: yup.array().of(
          yup.object().shape({
            cv: yup.mixed().required('Please upload a CV'),
          })
        ),
        fieldMappings: yup
          .array()
          .default([])
          .test(
            'fieldMappings',
            `please select all mandatory columns ${missingFields?.current?.join(', ')}`,
            (value) => {
              missingFields.current = mandatoryColumns.filter((field) => !value.includes(field));
              return !(missingFields?.current?.length > 0);
            }
          )
          .required(`Please map required fields ${missingFields?.current?.join(', ')}`),
      })
    ),
    defaultValues: {
      fieldMappings: fieldMappings ?? [],
      ignoreFirstRow: true,
    },
  });

  const {
    setValue,
    control,
    handleSubmit,
    register,
    clearErrors,
    formState: { errors },
  } = methods;

  const [openDialogs, setOpenDialogs] = useState<IDialogState>(initialDialogState);

  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(-1);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const columnOptions: WsSelectObj[] = [
    {
      label: 'SELECT',
      value: '',
    },
  ];

  const formattedSettings = submissionSetting
    ? (Object.entries(submissionSetting) as Entries<ISubmissionSettingsResponse>).filter(
        (obj) => !isEmpty(obj?.[1])
      )
    : [];

  /* Sorting values in order to list mandatory entries on the top of the list */

  formattedSettings.sort((obj1, obj2) => Number(obj1?.[1]?.required) - Number(obj2?.[1]?.required));

  formattedSettings.forEach((obj) => {
    columnOptions.push({
      label: `${obj?.[1]?.label} ${obj?.[1]?.required ? '*' : ''}`,
      value: obj?.[0] ?? '',
    });
  });

  columnOptions.push({
    label: 'RTR',
    value: 'rtr',
  });

  const processedRows = createRowsForSubmissions(candidatesData);
  const [, ...alteredRows] = processedRows;
  const [rows, setRows] = useState(alteredRows ?? []);

  const totalRows = (rows && rows[0]?.rowsValues?.length) ?? 0;
  useEffect(() => {
    setValue(`fieldMappings.${totalRows}`, 'uploadCV');
    setValue(`fieldMappings.${totalRows + 1}`, 'rtr');
    rows &&
      setValue('uploadCv', [
        ...rows.map((rowData) => {
          return { cv: rowData.location };
        }),
      ]);
  }, [rows, setValue, totalRows]);

  const deleteCV = async () => {
    try {
      if (fileId && selectedRowIndex > -1) {
        await deleteCvForSubmissions(selectedRowIndex, fileId);
        setNotification(success('CV deleted successfully !!'));
        setOpenDialogs({ ...openDialogs, deleteCVDialog: false });
        rows[selectedRowIndex].location = '';
        rows[selectedRowIndex].name = '';
        setRows([...rows]);
      } else {
        setNotification(failed('Few details are missing to delete CV, Please try again !!'));
      }
    } catch {
      setNotification(failed('Error in CV delete , Please try again !!'));
    }
  };

  const deleteRTR = async () => {
    try {
      if (fileId && selectedRowIndex > -1) {
        await deleteRtrForSubmissions(selectedRowIndex, fileId);
        setNotification(success('RTR deleted successfully !!'));
        setOpenDialogs({ ...openDialogs, deleteRtrDialog: false });
        rows[selectedRowIndex].rtrFileLocation = '';
        rows[selectedRowIndex].rtrFileOriginalName = '';
        setRows([...rows]);
      } else {
        setNotification(failed('Few details are missing to delete RTR, Please try again !!'));
      }
    } catch {
      setNotification(failed('Error in RTR delete , Please try again !!'));
    }
  };

  const handleToggle = (checked: boolean) => {
    setValue('ignoreFirstRow', checked);
    setRows(checked ? alteredRows : processedRows);
  };

  const uploadFile = async (file: File[], index: number) => {
    clearErrors('uploadCv');
    if (!file) return;
    const cv = await uploadFileToS3(file, setNotification);
    const result = await saveUploadedCV(cv, index, fileId, setNotification);
    if (result == 1) {
      rows[index].location = cv.location;
      rows[index].name = cv.name;
      setRows([...rows]);
    }
  };

  const updateRTRToRows = (rtr: IRightToRepresent, rowIndex: number) => {
    rows[rowIndex].rtrFileLocation = rtr.rtrFileLocation;
    rows[rowIndex].rtrFileOriginalName = rtr.rtrFileOriginalName;
    rows[rowIndex].rtrConsent = rtr.rtrConsent;
    setRows([...rows]);
  };

  const onSubmit = (data) => {
    getFieldMappingValues(data.fieldMappings, data.ignoreFirstRow);
    setOpen(false);
  };
  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {errors.fieldMappings && (
          <FormHelperText error>{errors.fieldMappings['message']}</FormHelperText>
        )}
        <Grid container justifyContent="flex-end">
          <WsTypography>Ignore First Row</WsTypography>
          <Controller
            name="ignoreFirstRow"
            control={control}
            render={(props) => (
              <Grid ml={1}>
                <Switch
                  defaultChecked={props.field.value}
                  onChange={(_event, checked) => handleToggle(checked)}
                />
              </Grid>
            )}
          />
        </Grid>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650, minHeight: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {rows &&
                  rows.length > 0 &&
                  rows[0]?.rowsValues?.map((row, index) => (
                    <TableCell key={index}>
                      <Box minWidth={130}>
                        <WsSelect
                          name={`fieldMappings.${index}`}
                          label={''}
                          defaultValue=""
                          selectArray={columnOptions}
                          size="small"
                          required={columnOptions[index].label.includes('*')}
                        />
                      </Box>
                    </TableCell>
                  ))}
                <TableCell>
                  <Box minWidth={130}>
                    <WsSelect
                      key="uploadCV"
                      name={`fieldMappings[${totalRows}]`}
                      label={''}
                      defaultValue="uploadCV"
                      disabled
                      size="small"
                      showMandatorySymbol={true}
                      selectArray={columnOptions}
                      required
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box minWidth={130}>
                    <WsSelect
                      key="rtr"
                      name={`fieldMappings[${totalRows + 1}]`}
                      label={''}
                      defaultValue="rtr"
                      disabled
                      size="small"
                      selectArray={columnOptions}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows &&
                rows?.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row &&
                      row?.rowsValues?.map((rowData, rowDataIndex) => (
                        <TableCell
                          key={`${rowDataIndex}_${rowIndex}`}
                          padding="none"
                          align="center"
                        >
                          <WsTypography
                            color={WsTypoColors.BLACK}
                            bold={rowIndex === 0}
                            variant={WsTypoVariants.BODY2}
                          >
                            {rowData}
                          </WsTypography>
                        </TableCell>
                      ))}
                    <TableCell padding="none" align="center">
                      {rows[rowIndex].location ? (
                        <>
                          <WsTypography> {`${rows[rowIndex].name ?? ''}`}</WsTypography>
                          <WsFilePreview
                            url={rows[rowIndex]?.location}
                            requireDownloadIcon={true}
                            onDownload={() =>
                              downloadCV(rows[rowIndex]?.location, rows[rowIndex]?.name)
                            }
                            hideViewIcon={true}
                            requireCloseIcon={true}
                            onClose={() => {
                              setSelectedRowIndex(rowIndex);
                              setOpenDialogs({ ...openDialogs, deleteCVDialog: true });
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <WsFileUpload
                            bgRequired={false}
                            onUpload={(file) => uploadFile(file, rowIndex)}
                            multiple={false}
                            maxFileSize={5}
                            placeholderText=""
                            {...register(`uploadCv.${rowIndex}.cv`, { required: true })}
                          />
                          <Grid container justifyContent="center" alignItems="center">
                            {errors?.uploadCv &&
                              errors.uploadCv[rowIndex] &&
                              errors.uploadCv[rowIndex].cv && (
                                <FormHelperText error>
                                  {errors.uploadCv[rowIndex].cv.message}
                                </FormHelperText>
                              )}
                          </Grid>
                        </>
                      )}
                    </TableCell>
                    <TableCell padding="none" align="center">
                      {rows[rowIndex]?.rtrFileLocation ? (
                        <>
                          <WsTypography>{`${
                            rows[rowIndex].rtrFileOriginalName ?? ''
                          }`}</WsTypography>
                          <WsFilePreview
                            url={rows[rowIndex]?.rtrFileLocation}
                            requireDownloadIcon={true}
                            onDownload={() =>
                              downloadCV(
                                rows[rowIndex]?.rtrFileLocation,
                                rows[rowIndex]?.rtrFileOriginalName
                              )
                            }
                            onViewClick={() => onCVViewClick(rows[rowIndex]?.rtrFileLocation)}
                            requireCloseIcon={true}
                            onClose={() => {
                              setSelectedRowIndex(rowIndex);
                              setOpenDialogs({ ...openDialogs, deleteRtrDialog: true });
                            }}
                          />
                        </>
                      ) : (
                        <RTR
                          rowIndex={rowIndex}
                          fileId={fileId}
                          refresh={(rtr) => updateRTRToRows(rtr, rowIndex)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid display="flex" justifyContent="flex-end" pt={2}>
          <WsButton type="submit">Validate</WsButton>
        </Grid>

        {openDialogs.deleteCVDialog && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <ConfirmDeleteDialog
              open={openDialogs.deleteCVDialog}
              onClose={() => setOpenDialogs({ ...openDialogs, deleteCVDialog: false })}
              onConfirm={deleteCV}
              deletingItem={`${rows[selectedRowIndex]?.name ?? 'CV'}`}
            />
          </ErrorBoundary>
        )}

        {openDialogs.deleteRtrDialog && (
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <ConfirmDeleteDialog
              open={openDialogs.deleteRtrDialog}
              onClose={() => setOpenDialogs({ ...openDialogs, deleteRtrDialog: false })}
              onConfirm={deleteRTR}
              deletingItem={`${rows[selectedRowIndex]?.rtrFileOriginalName ?? ''}`}
            />
          </ErrorBoundary>
        )}
      </Box>
    </FormProvider>
  );
};

export default ImportSpreadSheetColumns;
